/**
 * ContactSyncSettings — provider-agnostic contact sync UI (iter 291).
 *
 * Replaces the Google-specific GoogleImportDialog with a provider-list
 * layout. Google is the only "live" provider today; Outlook/CSV/Apple
 * cards render as "Coming soon".
 *
 * Features:
 *   • Provider list (connected/available with status pill).
 *   • Company-sync toggle (receives shared team contacts).
 *   • "Sync now" button — runs POST /contact-sync/pull-now.
 *   • "Push my existing contacts" button — runs push-existing backfill.
 *   • Recent sync activity — GET /contact-sync/log?limit=20.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { startGoogleOAuth } from '../utils/googleOAuth';
import { Loader2, RefreshCw, ShieldCheck, Users, Globe, AlertTriangle } from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Alert, AlertDescription } from '../components/ui/alert';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ContactSyncSettings({ open, onOpenChange }) {
  const [providers, setProviders] = useState([]);
  const [prefs, setPrefs] = useState({ company_sync_enabled: false, first_backfill_done: false });
  const [savingPrefs, setSavingPrefs] = useState(false);
  const [pulling, setPulling] = useState(false);
  const [pushing, setPushing] = useState(false);
  const [log, setLog] = useState([]);
  const [logLoading, setLogLoading] = useState(false);
  const [pushResult, setPushResult] = useState(null);
  const [pullResult, setPullResult] = useState(null);
  // Iter 291 — admin-only category share defaults section.
  const [isAdmin, setIsAdmin] = useState(false);
  const [companyDefaults, setCompanyDefaults] = useState(null);
  const [savingDefaults, setSavingDefaults] = useState(false);

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const [p, u, me] = await Promise.all([
          axios.get(`${API}/contact-sync/providers`),
          axios.get(`${API}/contact-sync/user-prefs`),
          axios.get(`${API}/auth/me`).catch(() => ({ data: {} })),
        ]);
        setProviders(p.data?.providers || []);
        setPrefs(u.data || { company_sync_enabled: false });
        const adminy = !!(me.data?.is_super_admin || ['admin', 'principal_designer'].includes(me.data?.role));
        setIsAdmin(adminy);
        if (adminy) {
          try {
            const cd = await axios.get(`${API}/contact-sync/company-defaults`);
            setCompanyDefaults(cd.data || null);
          } catch { /* non-fatal */ }
        }
      } catch (err) {
        console.error('[ContactSyncSettings] load failed:', err);
      }
    })();
  }, [open]);

  const saveCategoryDefault = async (category, next) => {
    if (!companyDefaults) return;
    const nextMap = { ...(companyDefaults.category_share_defaults || {}), [category]: next };
    setSavingDefaults(true);
    try {
      await axios.put(`${API}/contact-sync/company-defaults`, {
        category_share_defaults: nextMap,
        allow_per_contact_override: !!companyDefaults.allow_per_contact_override,
      });
      setCompanyDefaults(c => ({ ...c, category_share_defaults: nextMap }));
      toast.success(`${category} → ${next}`);
    } catch (err) {
      toast.error(`Could not save: ${err.response?.data?.detail || err.message}`);
    } finally {
      setSavingDefaults(false);
    }
  };

  const toggleAllowOverride = async (next) => {
    if (!companyDefaults) return;
    setSavingDefaults(true);
    try {
      await axios.put(`${API}/contact-sync/company-defaults`, {
        category_share_defaults: companyDefaults.category_share_defaults || {},
        allow_per_contact_override: next,
      });
      setCompanyDefaults(c => ({ ...c, allow_per_contact_override: next }));
    } catch (err) {
      toast.error(`Could not save: ${err.response?.data?.detail || err.message}`);
    } finally {
      setSavingDefaults(false);
    }
  };

  const setCompanySync = async (next) => {
    setSavingPrefs(true);
    try {
      const r = await axios.put(`${API}/contact-sync/user-prefs`, { company_sync_enabled: next });
      setPrefs(p => ({ ...p, company_sync_enabled: !!r.data?.company_sync_enabled }));
      toast.success(next
        ? 'Team contact sync ON — you will receive shared contacts.'
        : 'Team contact sync OFF — only contacts you create sync to your account.'
      );
    } catch (err) {
      toast.error(`Could not save: ${err.response?.data?.detail || err.message}`);
    } finally {
      setSavingPrefs(false);
    }
  };

  const runPull = async () => {
    setPulling(true);
    setPullResult(null);
    try {
      const r = await axios.post(`${API}/contact-sync/pull-now`);
      setPullResult(r.data);
      toast.success('Pulled latest changes from your provider(s).');
      loadLog();
    } catch (err) {
      toast.error(`Sync failed: ${err.response?.data?.detail || err.message}`);
    } finally {
      setPulling(false);
    }
  };

  const runPushExisting = async () => {
    setPushing(true);
    setPushResult(null);
    try {
      const r = await axios.post(`${API}/contact-sync/push-existing`, { all_shared: true });
      setPushResult(r.data);
      if (r.data?.pushed > 0) toast.success(`Pushed ${r.data.pushed} contact(s).`);
      else if (r.data?.considered === 0) toast.info('No contacts to push.');
      else toast.warning(`0 pushed of ${r.data?.considered}. See sync activity below.`);
      loadLog();
    } catch (err) {
      toast.error(`Push failed: ${err.response?.data?.detail || err.message}`);
    } finally {
      setPushing(false);
    }
  };

  const loadLog = async () => {
    setLogLoading(true);
    try {
      const r = await axios.get(`${API}/contact-sync/log?limit=20`);
      setLog(r.data?.entries || []);
    } catch (err) {
      console.error('[ContactSyncSettings] log fetch failed:', err);
    } finally {
      setLogLoading(false);
    }
  };

  const connectProvider = async (providerKey) => {
    if (providerKey !== 'google') {
      toast.info('This provider is coming soon.');
      return;
    }
    startGoogleOAuth({ redirectPath: window.location.pathname });
  };

  const googleProvider = providers.find(p => p.key === 'google');

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0F0F0F] border-[#2A2A2A] text-white max-w-2xl" data-testid="contact-sync-settings-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Globe className="w-5 h-5 text-[#70170F]" />
            Contact Sync
          </DialogTitle>
          <DialogDescription className="text-gray-400 text-xs">
            Keep KEY-MADE and your address book in step — automatically, both ways.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Provider list */}
          <div className="space-y-2" data-testid="contact-sync-providers">
            {providers.map(p => (
              <div key={p.key}
                   className="flex items-center justify-between p-3 rounded border border-[#2A2A2A] bg-[#141414]"
                   data-testid={`contact-sync-provider-${p.key}`}>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-white">{p.display_name}</div>
                  <div className="text-[11px] text-gray-500 mt-1">
                    {p.connected
                      ? `Connected as ${p.account_id || 'your account'}`
                      : p.status === 'coming_soon' ? 'Coming soon' : 'Not connected'}
                  </div>
                </div>
                {p.status === 'live' ? (
                  p.connected ? (
                    <span className="text-[11px] text-emerald-400 px-2 py-1 rounded bg-emerald-950/30" data-testid={`contact-sync-provider-${p.key}-connected`}>
                      Connected
                    </span>
                  ) : (
                    <Button size="sm" onClick={() => connectProvider(p.key)}
                            className="bg-[#70170F] hover:bg-[#70170F]/90 h-8 text-xs"
                            data-testid={`contact-sync-provider-${p.key}-connect`}>
                      Connect
                    </Button>
                  )
                ) : (
                  <span className="text-[11px] text-gray-500 px-2 py-1 rounded bg-[#1A1A1A]">
                    Soon
                  </span>
                )}
              </div>
            ))}
          </div>

          {/* Company sync toggle */}
          {googleProvider?.connected && (
            <div className="p-3 rounded border border-[#2A2A2A] bg-[#141414]"
                 data-testid="contact-sync-company-toggle-row">
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1">
                  <div className="text-sm font-medium">Sync team contacts</div>
                  <div className="text-[11px] text-gray-500 mt-1">
                    When ON, your account also receives shared contacts (Client, GC, Sub, Vendor, Inspector)
                    that your teammates create. When OFF, only contacts you personally create sync to your address book.
                  </div>
                </div>
                <label className="flex items-center gap-2 shrink-0">
                  <input type="checkbox"
                         className="h-4 w-4 accent-[#70170F]"
                         checked={!!prefs.company_sync_enabled}
                         disabled={savingPrefs}
                         onChange={e => setCompanySync(e.target.checked)}
                         data-testid="contact-sync-company-toggle" />
                  <span className="text-xs">{prefs.company_sync_enabled ? 'ON' : 'OFF'}</span>
                </label>
              </div>
            </div>
          )}

          {/* Actions */}
          {googleProvider?.connected && (
            <div className="grid grid-cols-2 gap-2">
              <Button onClick={runPull} disabled={pulling}
                      className="bg-[#70170F] hover:bg-[#70170F]/90 text-xs h-9"
                      data-testid="contact-sync-pull-btn">
                {pulling ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" />Syncing…</> :
                  <><RefreshCw className="w-3 h-3 mr-1" />Sync now (both ways)</>}
              </Button>
              <Button onClick={runPushExisting} disabled={pushing}
                      variant="outline"
                      className="border-[#2A2A2A] hover:bg-[#141414] text-xs h-9"
                      data-testid="contact-sync-push-existing-btn">
                {pushing ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" />Pushing…</> :
                  <><Users className="w-3 h-3 mr-1" />Push existing to my provider</>}
              </Button>
            </div>
          )}

          {pushResult && (
            <div className="text-[11px] text-gray-400" data-testid="contact-sync-push-result">
              Considered: <strong>{pushResult.considered}</strong> ·
              Pushed: <strong className="text-emerald-400">{pushResult.pushed}</strong> ·
              Skipped: <strong className="text-amber-400">{pushResult.skipped}</strong> ·
              Errors: <strong className="text-red-400">{pushResult.errors}</strong>
            </div>
          )}
          {pullResult && (
            <div className="text-[11px] text-gray-400" data-testid="contact-sync-pull-result">
              {Object.entries(pullResult.providers || {}).map(([k, v]) => (
                <div key={k}>
                  {k}: {v.changes_handled || 0} change(s), {v.deletions_handled || 0} deletion(s)
                  {v.was_full_snapshot ? ' (full snapshot)' : ''}
                </div>
              ))}
            </div>
          )}

          {/* Sync log */}
          <details className="p-3 rounded border border-[#2A2A2A] bg-[#141414]">
            <summary className="text-xs font-medium cursor-pointer" onClick={loadLog}
                     data-testid="contact-sync-log-toggle">
              Recent sync activity {log.length ? `(${log.length})` : ''}
            </summary>
            <div className="mt-2 space-y-1 max-h-48 overflow-y-auto" data-testid="contact-sync-log-list">
              {logLoading && <div className="text-[11px] text-gray-500">Loading…</div>}
              {!logLoading && log.length === 0 && (
                <div className="text-[11px] text-gray-500">
                  No entries yet. Create or edit a contact to see sync activity.
                </div>
              )}
              {log.map((row, i) => (
                <div key={i} className="text-[11px] font-mono flex items-center gap-2">
                  <span className={row.api_status === 'ok' ? 'text-emerald-400' : 'text-amber-300'}>
                    {row.api_status}
                  </span>
                  <span className="text-gray-500 truncate flex-1">
                    [{row.provider_key || 'google'}] {row.direction}/{row.action} ·{' '}
                    {row.resource_name || row.error_detail?.slice(0, 40) || '—'}
                  </span>
                </div>
              ))}
            </div>
          </details>

          {/* Iter 291 — admin-only category share defaults. Category
              controls whether a NEW contact defaults to shared or private.
              Users can override on a per-contact basis if allow_per_contact_override
              is on. */}
          {isAdmin && companyDefaults && (
            <details className="p-3 rounded border border-[#2A2A2A] bg-[#141414]" data-testid="contact-sync-admin-defaults">
              <summary className="text-xs font-medium cursor-pointer">
                Admin — Category share defaults
              </summary>
              <div className="mt-3 space-y-1">
                {Object.entries(companyDefaults.category_share_defaults || {}).map(([cat, val]) => (
                  <div key={cat} className="flex items-center justify-between text-xs" data-testid={`admin-share-default-${cat}`}>
                    <span className="text-gray-300">{cat}</span>
                    <select
                      value={val}
                      disabled={savingDefaults}
                      onChange={(e) => saveCategoryDefault(cat, e.target.value)}
                      className="bg-[#0F0F0F] border border-[#2A2A2A] rounded px-2 py-1 text-xs"
                    >
                      <option value="shared">Shared (team)</option>
                      <option value="private">Private (creator only)</option>
                    </select>
                  </div>
                ))}
                <label className="flex items-center gap-2 mt-2 pt-2 border-t border-[#2A2A2A]">
                  <input
                    type="checkbox"
                    className="h-4 w-4 accent-[#70170F]"
                    checked={!!companyDefaults.allow_per_contact_override}
                    disabled={savingDefaults}
                    onChange={e => toggleAllowOverride(e.target.checked)}
                    data-testid="admin-allow-override"
                  />
                  <span className="text-xs text-gray-300">
                    Allow users to override the default per contact
                  </span>
                </label>
              </div>
            </details>
          )}

          {/* Guarantees */}
          <Alert className="bg-[#1A1A1A] border-[#2A2A2A]">
            <ShieldCheck className="h-4 w-4 text-emerald-400" />
            <AlertDescription className="text-[11px] text-gray-400 space-y-1">
              <div>• Contacts you create sync automatically to your connected provider.</div>
              <div>• Shared contacts (Client/GC/Sub/Vendor/Inspector) fan out to team members who opted in.</div>
              <div>• Deleting from your provider only removes YOUR copy — the KEY-MADE record stays.</div>
              <div>• Project-linked contacts cannot be deleted from KEY-MADE.</div>
            </AlertDescription>
          </Alert>
        </div>
      </DialogContent>
    </Dialog>
  );
}
